import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject, signal } from '@angular/core';
import { VideoIppResponse } from '../types/video-ipp-response.type';

@Injectable({
  providedIn: 'root',
})
export class VideoDataService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly STORAGE_KEY = 'video_data';
  private readonly RUT_KEY = 'video_rut';
  private readonly videoData = signal<VideoIppResponse | null>(this.loadFromStorage());
  private readonly rut = signal<string | null>(this.loadRutFromStorage());

  private loadFromStorage(): VideoIppResponse | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }

    try {
      const stored = sessionStorage.getItem(this.STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error loading video data from storage:', error);
      return null;
    }
  }

  private loadRutFromStorage(): string | null {
    if (!isPlatformBrowser(this.platformId)) {
      return null;
    }
    try {
      return sessionStorage.getItem(this.RUT_KEY);
    } catch {
      return null;
    }
  }

  private saveToStorage(data: VideoIppResponse | null): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    try {
      if (data) {
        sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
      } else {
        sessionStorage.removeItem(this.STORAGE_KEY);
      }
    } catch (error) {
      console.error('Error saving video data to storage:', error);
    }
  }

  setVideoData(data: VideoIppResponse): void {
    this.videoData.set(data);
    this.saveToStorage(data);
  }

  getVideoData(): VideoIppResponse | null {
    return this.videoData();
  }

  setRut(rut: string): void {
    this.rut.set(rut);
    if (isPlatformBrowser(this.platformId)) {
      try {
        sessionStorage.setItem(this.RUT_KEY, rut);
      } catch { /* noop */ }
    }
  }

  getRut(): string | null {
    return this.rut();
  }

  clearVideoData(): void {
    this.videoData.set(null);
    this.rut.set(null);
    this.saveToStorage(null);
    if (isPlatformBrowser(this.platformId)) {
      try { sessionStorage.removeItem(this.RUT_KEY); } catch { /* noop */ }
    }
  }
}
